/**
 * Shared MSAL instance + Microsoft redirect detection.
 *
 * ## The ghost-login problem
 *
 * MSAL Browser stores the redirect authentication result in an in-memory
 * `redirectResponse` Map on the PublicClientApplication instance (keyed by
 * client ID). This map is populated once during `initialize()` and is NEVER
 * cleared — not by `clearCache()`, not by anything. Every call to
 * `handleRedirectPromise()` in the same SPA session returns the same cached
 * idToken, even after the user has logged out.
 *
 * Consequence: whenever LoginPage remounts (after logout, after a StrictMode
 * double-invoke, etc.) and calls `handleRedirectPromise()`, it gets back the
 * old idToken and fires `loginWithMicrosoft()` — silently logging the user
 * back in.
 *
 * ## The fix: sessionStorage-based redirect gate
 *
 * We set a sessionStorage key immediately before calling `loginRedirect()`.
 * SessionStorage survives the full-page Microsoft OAuth redirect round-trip,
 * is readable synchronously from the very first render (no bundle evaluation
 * race), and is shared across all module instances in the same tab.
 *
 * On return from Microsoft:
 *   - `isMicrosoftRedirectPending()` returns true → LoginPage initialises
 *     `ssoProcessing = true` from frame 0 → spinner shows immediately.
 *   - `consumeMicrosoftRedirect()` returns true exactly once (removes the key)
 *     → LoginPage calls `handleRedirectPromise()` and logs the user in.
 *
 * After that single consumption, every subsequent call returns false, so:
 *   - Post-logout SPA navigations   → handleRedirectPromise() is skipped ✓
 *   - React StrictMode 2nd mount    → handleRedirectPromise() is skipped ✓
 *   - Any other unexpected remount  → handleRedirectPromise() is skipped ✓
 *
 * The key is also cleared in `clearSession()` (AuthContext) so a logout
 * never leaves a stale flag behind.
 */
import { PublicClientApplication } from '@azure/msal-browser';

// ── Redirect gate (sessionStorage, survives full-page Microsoft redirect) ─────
//
// Two keys are used to keep the spinner and the ghost-login prevention
// independent of each other, which is critical for React StrictMode:
//
//  KEY 1  peak.msalRedirectPending
//    Set by : setMicrosoftRedirectPending() — immediately before loginRedirect()
//    Cleared: clearMicrosoftRedirectPending() — on success, failure, or logout
//    Meaning: "a Microsoft redirect round-trip is currently in progress"
//    Used by: isMicrosoftRedirectPending() → initialises ssoProcessing state
//
//  KEY 2  peak.msalHandled
//    Set by : consumeMicrosoftRedirect() — exactly once, by whichever mount
//             calls it first (mount 1 in StrictMode or the sole mount in prod)
//    Cleared: clearMicrosoftRedirectPending()
//    Meaning: "handleRedirectPromise() has already been claimed by one mount"
//    Used by: consumeMicrosoftRedirect() → gates handleRedirectPromise()
//
//  StrictMode double-mount behaviour:
//    Mount 1 effect: consumeMicrosoftRedirect() → KEY1 set, KEY2 not set
//                    → sets KEY2, returns true → calls handleRedirectPromise()
//    Mount 2 init  : isMicrosoftRedirectPending() → KEY1 still set → true
//                    → ssoProcessing starts true → spinner ✓
//    Mount 2 effect: consumeMicrosoftRedirect() → KEY2 already set → false
//                    → isMicrosoftRedirectPending() → true (KEY1 still set)
//                    → do NOT clear spinner, just wait for isAuthenticated ✓

const MSAL_REDIRECT_KEY = 'peak.msalRedirectPending';
const MSAL_HANDLED_KEY  = 'peak.msalHandled';

/** Call this immediately before `msalInstance.loginRedirect()`. */
export function setMicrosoftRedirectPending(): void {
  sessionStorage.setItem(MSAL_REDIRECT_KEY, '1');
  sessionStorage.removeItem(MSAL_HANDLED_KEY);
}

/**
 * Clears both redirect keys. Call on success, failure, or session teardown.
 */
export function clearMicrosoftRedirectPending(): void {
  sessionStorage.removeItem(MSAL_REDIRECT_KEY);
  sessionStorage.removeItem(MSAL_HANDLED_KEY);
}

/**
 * True while a Microsoft redirect round-trip is in progress.
 * Does NOT consume anything — safe to call from `useState` initialisers
 * and from the "should I keep the spinner?" check in the MSAL effect.
 */
export function isMicrosoftRedirectPending(): boolean {
  return sessionStorage.getItem(MSAL_REDIRECT_KEY) === '1';
}

/**
 * Claims the right to call `handleRedirectPromise()`.
 * Returns true exactly once per redirect — whichever mount calls this first
 * "wins" and marks KEY2 so every subsequent call (StrictMode 2nd mount, any
 * retry) returns false without touching KEY1 (the spinner flag).
 */
export function consumeMicrosoftRedirect(): boolean {
  if (
    sessionStorage.getItem(MSAL_REDIRECT_KEY) === '1' &&
    sessionStorage.getItem(MSAL_HANDLED_KEY) !== '1'
  ) {
    sessionStorage.setItem(MSAL_HANDLED_KEY, '1');
    return true;
  }
  return false;
}

// ── MSAL instance ────────────────────────────────────────────────────────────

const msalClientId = import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined;
const msalAuthority = import.meta.env.VITE_AZURE_DOMAIN as string | undefined;

export const msalInstance: PublicClientApplication | null =
  msalClientId && msalAuthority
    ? new PublicClientApplication({
        auth: {
          clientId: msalClientId,
          authority: msalAuthority,
          redirectUri: window.location.origin + '/login',
        },
        cache: { cacheLocation: 'sessionStorage' },
      })
    : null;

/**
 * Resolves once the MSAL instance is ready to handle redirects.
 * Awaited in LoginPage before calling handleRedirectPromise().
 */
export const msalReadyPromise: Promise<void> = msalInstance
  ? msalInstance.initialize()
  : Promise.resolve();
